import React from 'react';
import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = 'August 4, 2026';
const CONTACT_EMAIL = 'info@sanitylounge.com';

const LegalLayout = ({ title, children }) => (
  <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50">
    <header className="bg-white border-b border-gray-200">
      <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
        <Link to="/" className="text-lg font-bold text-purple-800">
          What's Happening Events
        </Link>
        <nav className="text-sm space-x-4">
          <Link to="/privacy" className="text-gray-600 hover:text-purple-700">Privacy Policy</Link>
          <Link to="/terms" className="text-gray-600 hover:text-purple-700">Terms of Use</Link>
        </nav>
      </div>
    </header>
    <main className="max-w-3xl mx-auto px-4 py-10">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">{title}</h1>
      <p className="text-sm text-gray-500 mb-8">Effective date: {EFFECTIVE_DATE}</p>
      <div className="space-y-6 text-gray-700 leading-relaxed text-[15px]">
        {children}
      </div>
      <footer className="mt-12 pt-6 border-t border-gray-200 text-sm text-gray-500">
        Questions? Contact us at{' '}
        <a href={`mailto:${CONTACT_EMAIL}`} className="text-purple-700 underline">{CONTACT_EMAIL}</a>.
      </footer>
    </main>
  </div>
);

const H2 = ({ children }) => (
  <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3">{children}</h2>
);

const H3 = ({ children }) => (
  <h3 className="text-base font-semibold text-gray-900 mt-5 mb-2">{children}</h3>
);

const UL = ({ children }) => (
  <ul className="list-disc pl-6 space-y-1">{children}</ul>
);

export const PrivacyPolicyPage = () => (
  <LegalLayout title="Privacy Policy">
    <p>
      This Privacy Policy describes how What's Happening Events ("we", "us", or "our")
      collects, uses, and shares information when you use our services: the What's
      Happening Events website and web application (whatshappeningevents.com and
      app.whatshappeningevents.com), calendars we power on our customers' own domains,
      and the What's Happening mobile applications for iOS and Android (together, the
      "Services").
    </p>

    <H2>Information We Collect</H2>

    <H3>Account information</H3>
    <p>
      When an organization member creates an account or signs in, we collect an email
      address, first and last name, and a password. Passwords are stored only as salted
      bcrypt hashes — we never store your password in plain text. If you sign in with
      Google, we receive your Google account identifier, name, email address, and
      profile photo URL from Google instead of a password.
    </p>

    <H3>Event submissions</H3>
    <p>
      When you submit an event to a public calendar without an account, we collect the
      contact details you provide: your name, email address, optionally a phone number,
      and any organization name, website, or description you include. This information
      is used by the calendar's administrators to review your submission and contact
      you about it, and is retained so administrators can recognize repeat submitters.
    </p>

    <H3>Content you provide</H3>
    <p>
      Events, images, organization profiles, and other content you create through the
      Services are stored by us. Published events (including titles, descriptions,
      dates, venues, host names, images, and ticket links) are public and are visible
      to anyone through calendars, embeds, our mobile apps, and our API.
    </p>

    <H3>Automatically collected information</H3>
    <UL>
      <li>
        <strong>Sign-in and security data.</strong> When you sign in or verify your
        email we record your IP address, browser/device information (user agent), and
        session identifiers. We use this to secure accounts, detect suspicious
        activity, and enforce login-attempt limits.
      </li>
      <li>
        <strong>Event analytics.</strong> When you view an event page or click a
        ticket link, we record the view or click together with your IP address, user
        agent, referrer, and a session identifier. We use this to show organizers
        aggregate statistics about their events.
      </li>
      <li>
        <strong>Web analytics.</strong> Our web application uses PostHog, a product
        analytics service, to understand how the web app is used (for example, page
        views, clicks, and device information). PostHog is configured to create
        identified profiles only for signed-in users. Our mobile apps do <strong>not</strong>{' '}
        include PostHog or any other third-party analytics or advertising SDK.
      </li>
    </UL>

    <H3>Local storage on your device</H3>
    <p>
      The web application stores authentication tokens and your currently selected
      organization in your browser's local storage. The mobile apps store
      authentication tokens in your device's secure storage (iOS Keychain / Android
      Keystore). PostHog sets its own cookies or local-storage entries in the web
      application. We do not use advertising cookies.
    </p>

    <H3>Information the mobile apps collect</H3>
    <p>
      The What's Happening mobile apps let anyone browse published events without an
      account, and let organization members sign in to manage their events. The apps
      collect only the sign-in credentials you enter (sent securely to our servers)
      and the standard request data described above. The apps do not access your
      location, contacts, camera, photos, or microphone, and contain no advertising.
    </p>

    <H2>How We Use Information</H2>
    <UL>
      <li>To provide, operate, and maintain the Services;</li>
      <li>To authenticate you and keep your account secure;</li>
      <li>To let calendar administrators review and manage event submissions;</li>
      <li>To send transactional emails such as email verification, password resets, organization invitations, and submission notifications;</li>
      <li>To provide organizers with aggregate analytics about their events;</li>
      <li>To automatically suggest categories, hosts, and venues for submitted events;</li>
      <li>To understand usage of the Services and improve them; and</li>
      <li>To comply with legal obligations and enforce our Terms of Use.</li>
    </UL>
    <p>We do not sell your personal information, and we do not use it for third-party advertising.</p>

    <H2>How We Share Information</H2>
    <p>We share information only as needed to run the Services:</p>
    <UL>
      <li>
        <strong>Service providers.</strong> We use Twilio SendGrid to deliver
        transactional email (they receive your email address and name), Google Cloud
        Storage to host uploaded images, Google to provide optional Google Sign-In,
        and PostHog for web analytics. We use OpenAI's API to automatically categorize
        events: the event's title and description (and our existing category, host,
        and venue names) are sent for matching; submitter contact details are not
        included in prompts, though host records may include a contact email.
      </li>
      <li>
        <strong>Calendar administrators.</strong> If you submit an event to an
        organization's calendar, that organization's administrators can see your
        submission and the contact details you provided.
      </li>
      <li>
        <strong>Organization-configured integrations.</strong> Organizations can
        configure webhooks and integrations (for example, WordPress, calendar feeds,
        or Slack notifications) that transmit event data — which may include submitter
        contact details — to endpoints the organization chooses. Those endpoints are
        controlled by the organization, not by us.
      </li>
      <li>
        <strong>Legal.</strong> We may disclose information if required by law or to
        protect the rights, safety, or property of our users or the public.
      </li>
    </UL>

    <H2>Data Retention</H2>
    <p>
      We retain account information for as long as your account exists. Event content
      remains until the organization or an administrator deletes it. Sign-in,
      verification, and analytics records are retained to maintain security and
      provide statistics. You may request deletion at any time as described below.
    </p>

    <H2>Your Rights and Choices</H2>
    <p>
      You may access and update your account information in the app's settings. You
      may request a copy of your personal information, ask us to correct it, or ask us
      to delete your account and associated personal information by emailing{' '}
      <a href={`mailto:${CONTACT_EMAIL}`} className="text-purple-700 underline">{CONTACT_EMAIL}</a>{' '}
      from the email address on the account. We will act on verified requests within 30
      days. Depending on where you live (for example, the EEA, UK, or California), you
      may have additional rights under local law, including the right to lodge a
      complaint with a supervisory authority; we honor such requests as required by
      the law that applies to you.
    </p>

    <H2>Security</H2>
    <p>
      All traffic to the Services is encrypted in transit using HTTPS. Passwords are
      hashed with bcrypt. Mobile sign-in tokens are kept in the operating system's
      secure storage. No method of transmission or storage is completely secure, but
      we work to protect your information using industry-standard practices.
    </p>

    <H2>Children</H2>
    <p>
      The Services are not directed to children under 13, and we do not knowingly
      collect personal information from children under 13. If you believe a child has
      provided us personal information, contact us and we will delete it.
    </p>

    <H2>Changes to This Policy</H2>
    <p>
      We may update this Privacy Policy from time to time. We will post the updated
      version on this page and update the effective date above. Material changes will
      be communicated through the Services or by email.
    </p>

    <H2>Contact Us</H2>
    <p>
      What's Happening Events —{' '}
      <a href={`mailto:${CONTACT_EMAIL}`} className="text-purple-700 underline">{CONTACT_EMAIL}</a>
    </p>
  </LegalLayout>
);

export const TermsOfUsePage = () => (
  <LegalLayout title="Terms of Use">
    <p>
      These Terms of Use ("Terms") govern your use of the What's Happening Events
      website and web application (whatshappeningevents.com and
      app.whatshappeningevents.com), calendars we power on our customers' domains, and
      the What's Happening mobile applications for iOS and Android (together, the
      "Services"), operated by What's Happening Events ("we", "us", or "our"). By
      using the Services, you agree to these Terms and to our{' '}
      <Link to="/privacy" className="text-purple-700 underline">Privacy Policy</Link>.
      If you do not agree, do not use the Services.
    </p>

    <H2>1. The Services</H2>
    <p>
      The Services let the public discover and browse published events, let anyone
      submit events to participating organizations' calendars for review, and let
      organizations and their members manage calendars, events, and related settings.
    </p>

    <H2>2. Accounts</H2>
    <p>
      Organization accounts are created through the web application. You are
      responsible for maintaining the confidentiality of your credentials and for all
      activity under your account. You must provide accurate information and be at
      least 13 years old to use the Services (or older where local law requires). You
      may not share credentials or impersonate another person or organization. Notify
      us promptly of any unauthorized use of your account.
    </p>

    <H2>3. Your Content</H2>
    <p>
      You retain ownership of the content you submit — events, descriptions, images,
      and profile information ("Content"). By submitting Content, you grant us a
      worldwide, non-exclusive, royalty-free license to host, store, reproduce,
      display, distribute, and adapt (for formatting purposes) that Content in order
      to operate and promote the Services, including displaying published events
      publicly through calendars, embeds, feeds, the mobile apps, and our API, and
      distributing them through integrations that organizations configure. This
      license ends when the Content is deleted from the Services, except where it has
      already been distributed publicly or through integrations, or where retention is
      required by law.
    </p>
    <p>You represent that you have the rights necessary to submit your Content and that it does not infringe anyone's rights.</p>

    <H2>4. Acceptable Use</H2>
    <p>You agree not to:</p>
    <UL>
      <li>Post Content that is unlawful, deceptive, defamatory, obscene, harassing, hateful, or that infringes intellectual-property, privacy, or publicity rights;</li>
      <li>Submit false, misleading, or fraudulent events, or events you are not authorized to publicize;</li>
      <li>Attempt to gain unauthorized access to the Services, other accounts, or our systems, or probe or test their vulnerability without authorization;</li>
      <li>Interfere with the operation of the Services, including by introducing malware, scraping at disruptive volumes, circumventing rate limits, or overloading our infrastructure;</li>
      <li>Use the Services to send spam or unsolicited communications; or</li>
      <li>Use the Services in violation of applicable law.</li>
    </UL>
    <p>
      We may remove Content, reject submissions, or suspend or terminate accounts that
      violate these Terms.
    </p>

    <H2>5. Event Submissions and Moderation</H2>
    <p>
      Event submissions are reviewed by the receiving organization's administrators,
      who decide in their sole discretion whether to publish, edit, or reject them.
      We and participating organizations are not obligated to publish any submission.
      Organizations are responsible for the calendars they operate, including their
      moderation decisions and any integrations they configure.
    </p>

    <H2>6. Third-Party Content and Links</H2>
    <p>
      Events may include links to third-party websites, such as ticketing pages,
      venue maps, or organizer websites. We do not control and are not responsible for
      third-party sites, their content, or their transactions. Ticket purchases and
      other transactions occur solely between you and the third party. Event details
      are provided by organizers and submitters; we do not guarantee their accuracy,
      and events may be changed or cancelled by their organizers at any time.
    </p>

    <H2>7. Intellectual Property</H2>
    <p>
      The Services, including their software, design, and branding, are owned by us or
      our licensors and are protected by intellectual-property laws. We grant you a
      limited, non-exclusive, non-transferable, revocable license to use the Services,
      including the mobile apps on devices you own or control, for their intended
      purpose. You may not copy, modify, distribute, sell, lease, or reverse engineer
      any part of the Services except as permitted by law.
    </p>

    <H2>8. Copyright Complaints</H2>
    <p>
      If you believe Content on the Services infringes your copyright, send a notice
      to{' '}
      <a href={`mailto:${CONTACT_EMAIL}`} className="text-purple-700 underline">{CONTACT_EMAIL}</a>{' '}
      identifying the work, the infringing material and its location, your contact
      information, a statement of good-faith belief that the use is unauthorized, and
      a statement, under penalty of perjury, that the information in your notice is
      accurate and that you are the owner or authorized to act for the owner. We will
      respond to valid notices, including removing infringing material where
      appropriate.
    </p>

    <H2>9. Termination</H2>
    <p>
      You may stop using the Services at any time and may request deletion of your
      account as described in the Privacy Policy. We may suspend or terminate your
      access to the Services at any time for violation of these Terms, risk to other
      users or to us, or discontinuation of the Services. Sections that by their
      nature should survive termination (including Sections 3, 6, 10, 11, and 12)
      survive.
    </p>

    <H2>10. Disclaimers</H2>
    <p>
      THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY
      KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
      PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE
      SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE, OR THAT EVENT INFORMATION
      IS ACCURATE OR CURRENT.
    </p>

    <H2>11. Limitation of Liability</H2>
    <p>
      TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE WILL NOT BE LIABLE FOR ANY INDIRECT,
      INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF
      PROFITS, DATA, OR GOODWILL, ARISING OUT OF OR RELATING TO YOUR USE OF THE
      SERVICES. OUR TOTAL LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATING TO THE
      SERVICES WILL NOT EXCEED THE GREATER OF ONE HUNDRED U.S. DOLLARS (US $100) OR
      THE AMOUNTS YOU PAID US FOR THE SERVICES IN THE TWELVE MONTHS BEFORE THE CLAIM.
      SOME JURISDICTIONS DO NOT ALLOW CERTAIN LIMITATIONS, SO SOME OF THESE
      LIMITATIONS MAY NOT APPLY TO YOU.
    </p>

    <H2>12. Indemnification</H2>
    <p>
      You agree to indemnify and hold us harmless from claims, damages, and expenses
      (including reasonable attorneys' fees) arising from your Content, your use of
      the Services, or your violation of these Terms or applicable law.
    </p>

    <H2>13. Mobile App Stores</H2>
    <p>
      The mobile apps are distributed through the Apple App Store and Google Play.
      Your use of the apps is also subject to the applicable store's terms. Apple and
      Google are not parties to these Terms, have no obligation to provide support or
      maintenance for the apps, and are not responsible for addressing any claims
      relating to the apps; Apple and its subsidiaries are third-party beneficiaries
      of these Terms with respect to your use of the iOS app.
    </p>

    <H2>14. Changes to the Services and These Terms</H2>
    <p>
      We may modify or discontinue the Services at any time. We may update these Terms
      from time to time; the updated Terms will be posted on this page with a new
      effective date, and material changes will be communicated through the Services
      or by email. Continued use of the Services after changes take effect constitutes
      acceptance of the updated Terms.
    </p>

    <H2>15. Governing Law and Disputes</H2>
    <p>
      These Terms are governed by the laws of the United States and the state in which
      What's Happening Events is organized, without regard to conflict-of-law rules.
      Before filing a claim, you agree to contact us and attempt to resolve the
      dispute informally. Any dispute that cannot be resolved informally will be
      brought in the state or federal courts of that state, and you consent to their
      jurisdiction.
    </p>

    <H2>16. Contact</H2>
    <p>
      What's Happening Events —{' '}
      <a href={`mailto:${CONTACT_EMAIL}`} className="text-purple-700 underline">{CONTACT_EMAIL}</a>
    </p>
  </LegalLayout>
);
